import type { FuelType } from "@autoflow/types";
import { http } from "../../shared/api/http";
import type { VehicleListResponse, VehicleRecord } from "./vehicle-types";

export type VehicleInput = {
  customerId: string;
  vin: string;
  licensePlate?: string;
  year: number;
  make: string;
  model: string;
  engine?: string;
  transmission?: string;
  mileage?: number;
  color?: string;
  fuelType?: FuelType;
  notes?: string;
};

export type VinDecodeResult = {
  vin: string;
  year: number | null;
  make: string | null;
  model: string | null;
  trim: string | null;
  series: string | null;
  bodyClass: string | null;
  vehicleType: string | null;
  manufacturer: string | null;
  plantCity: string | null;
  plantState: string | null;
  plantCountry: string | null;
  engineModel: string | null;
  engineCylinders: string | null;
  displacementL: string | null;
  fuelType: string | null;
  transmission: string | null;
  transmissionSpeeds: string | null;
  driveType: string | null;
  doors: string | null;
  gvwr: string | null;
  brakeSystem: string | null;
  abs: string | null;
  esc: string | null;
  tractionControl: string | null;
  tpms: string | null;
  errorCode: string | null;
  errorText: string | null;
};

export const vehiclesApi = {
  async list(search = "", customerId?: string, page = 1) {
    return (await http.get<VehicleListResponse>("/v1/vehicles", {
      params: { search: search || undefined, customerId, page, pageSize: 20 }
    })).data;
  },
  async get(id: string) {
    return (await http.get<VehicleRecord>(`/v1/vehicles/${id}`)).data;
  },
  async decodeVin(vin: string) {
    return (await http.get<VinDecodeResult>(`/v1/vehicles/decode-vin/${encodeURIComponent(vin)}`)).data;
  },
  async create(input: VehicleInput) {
    return (await http.post<VehicleRecord>("/v1/vehicles", input)).data;
  },
  async update(id: string, input: Partial<VehicleInput>) {
    return (await http.patch<VehicleRecord>(`/v1/vehicles/${id}`, input)).data;
  },
  async remove(id: string) {
    await http.delete(`/v1/vehicles/${id}`);
  },
  async uploadPhoto(id: string, file: File, caption?: string) {
    const form = new FormData();
    form.append("file", file);
    if (caption) form.append("caption", caption);
    return (await http.post(`/v1/vehicles/${id}/photos`, form, {
      headers: { "Content-Type": "multipart/form-data" }
    })).data;
  },
  async removePhoto(id: string, photoId: string) {
    await http.delete(`/v1/vehicles/${id}/photos/${photoId}`);
  }
};
